/**
 * The single upload-type registry: labels, descriptions, template columns,
 * accepted formats, and lifecycle colors — so the upload portal, templates,
 * history, detail, and duplicate-conflict views render from one source.
 *
 * This used to be spread across four hand-maintained copies (the portal's
 * card config, the templates page's TemplateMeta, the history page's
 * TYPE_LABEL/TYPE_OPTIONS, and this file's label map), which is how the
 * templates page ended up advertising column lists the backend never had.
 * `columns` below is the EXACT template header from the backend registry
 * (omai `src/compliance/uploads/schemas.py`) — the same tuple auto-detection
 * matches an uploaded file against, so what this page shows is what the
 * detector expects, byte for byte.
 */
import { DEFAULT_BRANDING, type OrganizationBranding } from "@compliance/domain";
import type { UploadIngestResult, UploadStatus, UploadType } from "../api/types";

export type UploadTypeMeta = {
  label: string;
  /** Compact form for dense tables and filter dropdowns. */
  shortLabel: string;
  description: string;
  /** The exact template header (backend `UploadSchema.header`). */
  columns: readonly string[];
  /** MIME → extension(s), for controls that upload this type directly. */
  accept: Record<string, string[]>;
  maxSizeMb: number;
};

const CSV_ACCEPT: Record<string, string[]> = { "text/csv": [".csv"] };

/**
 * The registry, labelled for one organization.
 *
 * The two custody-statement types name the utility that issues them. That was
 * the literal "PG&E" — right for the reference customer and wrong for every
 * other organization on the same deployment — so the label now comes from the
 * organization's branding (`/me`), and the wire key `pge_statement` stays what
 * it is. Structure (columns, accepted formats, sizes) is the same for everyone.
 */
export function uploadTypeMeta(
  branding: OrganizationBranding = DEFAULT_BRANDING,
): Record<UploadType, UploadTypeMeta> {
  const custody = branding.custody_party_label;
  const partner = branding.dispensing_partner_label;
  return {
    pge_statement: {
      label: `${custody} Monthly Statement`,
      shortLabel: `${custody} Statement`,
      description: `${custody} pipeline-injection energy statement (MMBtu/Dth, HHV basis). Rows are addends — the period total is their sum — so a daily statement carries many rows.`,
      columns: ["period", "meter", "volume", "unit", "basis"],
      accept: CSV_ACCEPT,
      maxSizeMb: 10,
    },
    pge_injection_volume: {
      label: `${custody} Injection Volume`,
      shortLabel: `${custody} Injection Volume`,
      description:
        "Custody statement showing injected volume by meter (scf or mscf). Rows are " +
        "addends, like the energy statement.",
      columns: ["period", "meter", "volume", "unit"],
      accept: CSV_ACCEPT,
      maxSizeMb: 10,
    },
    dairy_kwh: {
      label: "Per-Dairy kWh",
      shortLabel: "Per-Dairy kWh",
      description: "Monthly electrical consumption per dairy site, one row per dairy.",
      columns: ["dairy_id", "period", "kwh", "unit"],
      accept: CSV_ACCEPT,
      maxSizeMb: 10,
    },
    upgrading_kwh: {
      label: "Upgrading Facility kWh",
      shortLabel: "Upgrading kWh",
      description: "Monthly electrical consumption at the upgrading facility.",
      columns: ["facility_id", "period", "kwh", "unit"],
      accept: CSV_ACCEPT,
      maxSizeMb: 10,
    },
    dairy_manure: {
      label: "Per-Dairy Manure Lbs",
      shortLabel: "Manure Lbs",
      description: "Monthly wet manure lbs delivered to the digester, by dairy.",
      columns: ["dairy_id", "period", "quantity", "unit"],
      accept: CSV_ACCEPT,
      maxSizeMb: 10,
    },
    calibration_cert: {
      label: "Calibration Certificate",
      shortLabel: "Calibration",
      description:
        "Lab-issued chromatograph or flow-meter calibration cert. Drop the PDF and " +
        "give its dates — filing it records the calibration and clears any finding " +
        "it covers.",
      // A document, not a table: there are no columns to parse. The dates live on
      // the certificate rather than in the file, so the form beside the dropzone
      // collects them. (This entry used to advertise a CSV header while the
      // dropzone accepted PDF, and the Templates tab handed out a CSV the backend
      // then refused.)
      columns: [],
      accept: { "application/pdf": [".pdf"] },
      maxSizeMb: 10,
    },
    station_allocation: {
      // Named for what it fills — the FSE ledger — rather than for the file the
      // partner happens to send. The wire key stays `station_allocation`.
      label: "FSE Ledger Upload",
      shortLabel: "FSE Ledger",
      description: `The monthly workbook ${partner} sends, carrying each station's dispensed volume. Loaded on the LCFS screen. The file holds every month of the year to date; you choose which of its months to load.`,
      // A document, not a table. One sheet per month, and the site table sits
      // below a daily grid at a column offset that moves when a dispenser group
      // is added — so there is no header for the generic detector to match and
      // no template anyone fills in.
      columns: [],
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      },
      maxSizeMb: 10,
    },
    utility_statement: {
      label: "Utility Statement",
      shortLabel: "Statement",
      description: `One station's monthly gas bill, which ${partner} sends with the ledger — the support behind that station's dispensed volume. Filed a station at a time from the LCFS screen, with the gas meter number read off the bill.`,
      // A document, not a table. Four utilities serve these stations and none of
      // them share a layout — one prints therms with no meter reads, another
      // prints CCF with a billing factor, and at least one arrives as a scan with
      // no text layer — so there is nothing for a detector to match.
      columns: [],
      accept: { "application/pdf": [".pdf"] },
      // Statements run to eight pages with scanned images; the largest seen so
      // far is under 1 MB, and the backend refuses past this too.
      maxSizeMb: 10,
    },
    transaction_confirmation: {
      label: "Transaction Confirmation",
      shortLabel: "Confirmation",
      description: `The executed Monthly RNG Confirmation from ${partner}. It states a quantity for every month of the contract term, so filing one checks each of those months against what this platform holds — a wrong figure for a month that closed long ago is the defect this catches.`,
      // A document, but unlike the statements this one IS parsed: one
      // counterparty, one template, a real text layer. Only the execution date
      // is asked of a person, because it orders revisions and nothing on the
      // document can be trusted to.
      columns: [],
      accept: { "application/pdf": [".pdf"] },
      maxSizeMb: 10,
    },
  };
}

/** Just the long labels, for one organization. */
export function uploadTypeLabels(
  branding: OrganizationBranding = DEFAULT_BRANDING,
): Record<UploadType, string> {
  return Object.fromEntries(
    Object.entries(uploadTypeMeta(branding)).map(([type, meta]) => [type, meta.label]),
  ) as Record<UploadType, string>;
}

/**
 * The reference-labelled registry. Right for structure (columns, formats) and
 * for anything not rendered to a person; a screen showing a custody-type label
 * reads `useUploadTypeMeta()` / `useUploadTypeLabels()` instead.
 */
export const UPLOAD_TYPE_META: Record<UploadType, UploadTypeMeta> = uploadTypeMeta();

/** Types filed as a document + fields, with no table to parse or template to fill. */
export const DOCUMENT_UPLOAD_TYPES: ReadonlySet<string> = new Set([
  "calibration_cert",
  "station_allocation",
  // Missing this is not cosmetic: the Templates tab would hand out a blank CSV
  // for a type the backend only accepts as a PDF. That exact pairing — a
  // dropzone taking PDFs and a templates row offering a CSV — is reason #2 the
  // old calibration-cert control could never have worked.
  "utility_statement",
  "transaction_confirmation",
]);

/** Display order — the periodic templates first, the event-based cert last. */
export const UPLOAD_TYPE_ORDER = [
  "pge_statement",
  "pge_injection_volume",
  "dairy_kwh",
  "upgrading_kwh",
  "dairy_manure",
  "calibration_cert",
  "station_allocation",
  "utility_statement",
  "transaction_confirmation",
] as const satisfies readonly UploadType[];

// Compile-time completeness: the maps built from ORDER are cast to
// Record<UploadType, …>, so a type added to UploadType but forgotten here
// must fail the build rather than render blank labels downstream.
type _MissingFromOrder = Exclude<UploadType, (typeof UPLOAD_TYPE_ORDER)[number]>;
const _orderIsComplete: _MissingFromOrder extends never ? true : never = true;
void _orderIsComplete;

export const UPLOAD_TYPE_LABEL: Record<UploadType, string> = uploadTypeLabels();

/** What the one auto-detecting button accepts: the union of the periodic
 *  templates' formats plus .xlsx (the backend reads workbooks since the
 *  one-button change). Calibration certs are excluded — see their meta. */
export const AUTO_DETECT_ACCEPT: Record<string, string[]> = {
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

/** Mirrors the backend's MAX_UPLOAD_BYTES (10 MB, enforced pre-intake as 413). */
export const AUTO_DETECT_MAX_MB = 10;

export const UPLOAD_STATUS_COLOR: Record<UploadStatus, string> = {
  // `received` is a persisted parse failure (the BE parses inline on POST), so
  // it reads as an attention state rather than an in-flight/neutral grey.
  received: "orange",
  parsed: "teal",
  accepted: "teal",
  rejected: "red",
  superseded: "yellow",
  // Terminal and successful, like `accepted`, but a different fact: the
  // document is on file rather than joined to a batch dataset. Blue keeps the
  // two from reading as the same outcome.
  filed: "blue",
};

/** A POST ingest/replace result that did NOT parse clean (backend leaves a
 *  parse-with-errors upload in `received` with parse_ok:false; `rejected` is
 *  the explicit reject). Either means "not accepted into the dataset". */
export function ingestFailed(r: UploadIngestResult): boolean {
  return !r.parse_ok || r.status === "rejected";
}
